<template>
  <div class="symmetry-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>P(x, y) → P'(x, -y) | 轴对称 | 中心对称$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="300" viewBox="0 0 500 300">
           <!-- Grid Background -->
           <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                 <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="1"/>
              </pattern>
           </defs>
           <rect width="100%" height="100%" fill="url(#grid)" />

           <!-- Mirror Axis -->
           <line x1="250" y1="20" x2="250" y2="280" stroke="#ffd700" stroke-width="2" stroke-dasharray="8,4" />
           <text x="255" y="30" fill="#ffd700" font-size="12">对称轴 (L)</text>

           <!-- Original Shape -->
           <g :opacity="isCenterSymmetry ? 1 : 0.8">
              <path :d="shapePath" fill="rgba(0, 210, 255, 0.3)" stroke="#00d2ff" stroke-width="2" />
              <circle v-for="(p, i) in points" :key="'op'+i" :cx="p.x" :cy="p.y" r="4" fill="#00d2ff" />
           </g>

           <!-- Symmetrical Shape -->
           <g v-if="!isCenterSymmetry">
              <path :d="mirroredPath" fill="rgba(255, 71, 87, 0.3)" stroke="#ff4757" stroke-width="2" stroke-dasharray="5,3" />
              <circle v-for="(p, i) in mirroredPoints" :key="'mp'+i" :cx="p.x" :cy="p.y" r="4" fill="#ff4757" />
              <!-- Correspondence lines -->
              <line v-for="(p, i) in points" :key="'cl'+i" :x1="p.x" :y1="p.y" :x2="mirroredPoints[i].x" :y2="mirroredPoints[i].y" 
                    stroke="rgba(255,255,255,0.2)" stroke-width="1" stroke-dasharray="2,2" />
           </g>

           <!-- Center Symmetry -->
           <g v-else>
              <circle cx="250" cy="150" r="5" fill="#ffd700" />
              <text x="255" y="165" fill="#ffd700" font-size="10">对称中心 O</text>
              <path :d="centerPath" fill="rgba(46, 213, 115, 0.3)" stroke="#2ed573" stroke-width="2" stroke-dasharray="5,3" />
              <circle v-for="(p, i) in centerPoints" :key="'cp'+i" :cx="p.x" :cy="p.y" r="4" fill="#2ed573" />
              <!-- Radius lines -->
              <line v-for="(p, i) in points" :key="'rl'+i" :x1="p.x" :y1="p.y" :x2="centerPoints[i].x" :y2="centerPoints[i].y" 
                    stroke="rgba(255,255,255,0.2)" stroke-width="1" stroke-dasharray="2,2" />
           </g>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>变换类型: {{ isCenterSymmetry ? '中心对称' : '轴对称' }}</label>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
           <div class="row items-center gap-4">
              <div class="flex-1">
                 <label>选择几何变换</label>
                 <el-radio-group v-model="transformType" size="mini">
                   <el-radio-button label="axial">轴对称</el-radio-button>
                   <el-radio-button label="center">中心对称</el-radio-button>
                 </el-radio-group>
              </div>
              <div class="flex-1">
                 <label>调节图形偏移 - 实时看对应点连线</label>
                 <el-slider v-model="offset" :min="-100" :max="0" size="mini"></el-slider>
              </div>
           </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>数学释义：</strong><br>
           <strong>轴对称：</strong> 如果一个图形沿着一条直线折叠后，直线两旁的部分能够互相重合。两个图形关于某直线对称，对应点连线被对称轴垂直平分。<br>
           <strong>中心对称：</strong> 如果一个图形绕某一点旋转180°后与另一个图形重合。对应点连线经过对称中心且被平分。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SymmetryViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      transformType: 'axial',
      offset: -60,
      basePoints: [
        { x: 0, y: -40 },
        { x: 50, y: 40 },
        { x: -30, y: 20 }
      ]
    }
  },
  computed: {
    isCenterSymmetry() { return this.transformType === 'center' || /中心对称/.test(this.knowledgePoint); },
    points() {
      const centerX = 250 + this.offset;
      const centerY = 150;
      return this.basePoints.map(p => ({ x: centerX + p.x, y: centerY + p.y }));
    },
    shapePath() { return 'M ' + this.points.map(p => `${p.x} ${p.y}`).join(' L ') + ' Z'; },
    
    mirroredPoints() {
      const axisX = 250;
      return this.points.map(p => ({ x: axisX + (axisX - p.x), y: p.y }));
    },
    mirroredPath() { return 'M ' + this.mirroredPoints.map(p => `${p.x} ${p.y}`).join(' L ') + ' Z'; },
    
    centerPoints() {
      const axisX = 250;
      const axisY = 150;
      return this.points.map(p => ({ x: axisX + (axisX - p.x), y: axisY + (axisY - p.y) }));
    },
    centerPath() { return 'M ' + this.centerPoints.map(p => `${p.x} ${p.y}`).join(' L ') + ' Z'; }
  },
  mounted() {
    if (/中心对称/.test(this.knowledgePoint)) {
      this.transformType = 'center';
    }
  }
}
</script>

<style scoped lang="scss">
.symmetry-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #010205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; overflow-y: auto; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(255, 215, 0, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
.row { display: flex; gap: 20px; align-items: center; }
label { font-size: 0.75rem; color: #888; margin-bottom: 5px; display: block; }
.flex-1 { flex: 1; }
</style>
